import { useState, useMemo, useEffect } from 'react';
import {
  Building2,
  Plus,
  Search,
  Pencil,
  Archive,
  ArchiveRestore,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import CompanyFormModal from './CompanyFormModal';

const PAGE_SIZE = 10;

export default function CompaniesPanel({ companies = [], onSaveCompany, onArchiveCompany, onFetchCompanies, total = 0 }) {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(''); // '' = todos, 'ativo', 'arquivado'
  const [page, setPage] = useState(1);
  const [formCompany, setFormCompany] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    onFetchCompanies?.({
      q: search.trim(),
      status: filterStatus,
      page,
      pageSize: PAGE_SIZE,
    });
  }, [search, filterStatus, page, onFetchCompanies]);

  const paginated = useMemo(() => companies, [companies]);
  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));

  const handleCreate = () => {
    setFormCompany(null);
    setShowForm(true);
  };

  const handleEdit = (company) => {
    setFormCompany(company);
    setShowForm(true);
  };

  const handleSaveCompany = async (data) => {
    await onSaveCompany?.(data);
    setShowForm(false);
    setFormCompany(null);
  };

  const handleArchive = async (company, archive) => {
    await onArchiveCompany?.(company.id, archive);
  };

  return (
    <div className="flex-1 flex flex-col bg-appPage overflow-hidden">
      <header className="px-8 py-6 border-b border-appSurfaceBorder bg-appSurface shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-appText tracking-tight">Empresas</h1>
            <p className="text-sm text-appTextMuted mt-1">
              {total} empresa(s) • Cadastre e gerencie empresas para vincular aos contatos
            </p>
          </div>
          <button
            type="button"
            onClick={handleCreate}
            className="flex items-center gap-2 px-5 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90 shadow-lg"
          >
            <Plus size={18} />
            Nova empresa
          </button>
        </div>

        <div className="flex flex-wrap gap-4 mt-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-appTextMuted" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar por nome fantasia, razão social ou CNPJ..."
              className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none bg-white"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            className="px-4 py-2.5 rounded-xl border border-appSurfaceBorder text-appText bg-white"
          >
            <option value="">Todos os status</option>
            <option value="ativo">Ativo</option>
            <option value="arquivado">Arquivado</option>
          </select>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-8">
        <div className="bg-appSurface rounded-2xl border border-appSurfaceBorder shadow-sm overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-appSurfaceBorder bg-appPage/50">
                <th className="px-4 py-3 text-xs font-bold text-appTextMuted uppercase">Nome fantasia</th>
                <th className="px-4 py-3 text-xs font-bold text-appTextMuted uppercase">Razão social</th>
                <th className="px-4 py-3 text-xs font-bold text-appTextMuted uppercase">CNPJ</th>
                <th className="px-4 py-3 text-xs font-bold text-appTextMuted uppercase">Status</th>
                <th className="w-24 px-4 py-3 text-xs font-bold text-appTextMuted uppercase text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((company) => (
                <tr
                  key={company.id}
                  className="border-b border-appSurfaceBorder/50 hover:bg-appPage/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    <span className="font-medium text-appText">{company.nome_fantasia || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-appText">{company.razao_social || '—'}</td>
                  <td className="px-4 py-3 text-sm text-appText">{company.cnpj || '—'}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        company.status === 'arquivado'
                          ? 'bg-slate-100 text-slate-600'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {company.status === 'arquivado' ? 'Arquivado' : 'Ativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => handleEdit(company)}
                        className="p-2 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-lg"
                        aria-label="Editar"
                      >
                        <Pencil size={16} />
                      </button>
                      {company.status === 'ativo' ? (
                        <button
                          type="button"
                          onClick={() => handleArchive(company, true)}
                          className="p-2 text-appTextMuted hover:bg-rose-50 hover:text-rose-600 rounded-lg"
                          aria-label="Arquivar"
                        >
                          <Archive size={16} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleArchive(company, false)}
                          className="p-2 text-appTextMuted hover:bg-emerald-50 hover:text-emerald-600 rounded-lg"
                          aria-label="Desarquivar"
                        >
                          <ArchiveRestore size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {paginated.length === 0 && (
            <div className="py-16 text-center text-appTextMuted">
              <Building2 size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-medium">Nenhuma empresa encontrada</p>
              <p className="text-sm mt-1">Ajuste os filtros ou cadastre uma nova empresa</p>
              <button
                type="button"
                onClick={handleCreate}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90"
              >
                <Plus size={18} />
                Nova empresa
              </button>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-appTextMuted">
              Página {page} de {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-2 rounded-lg border border-appSurfaceBorder text-appText disabled:opacity-50 disabled:cursor-not-allowed hover:bg-appSurfaceBorder/30"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-2 rounded-lg border border-appSurfaceBorder text-appText disabled:opacity-50 disabled:cursor-not-allowed hover:bg-appSurfaceBorder/30"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <CompanyFormModal
          company={formCompany}
          onSave={handleSaveCompany}
          onClose={() => { setShowForm(false); setFormCompany(null); }}
        />
      )}
    </div>
  );
}
